import type {
  CustomInterrogationListItem,
  PartInterrogationFile,
} from '@frontend/api';

import type { ActionsOf } from 'store/types';

import type {
  InterrogationMaterialQuery,
  InterrogationSelection,
} from './types';

export const types = {
  CANCEL_EDIT_INTERROGATION_SETUP:
    'partLibrary/viewer/geometricInterrogation/CANCEL_EDIT_INTERROGATION_SETUP',
  CLEAR_INTERROGATION:
    'partLibrary/viewer/geometricInterrogation/CLEAR_INTERROGATION',
  EDIT_INTERROGATION_SETUP:
    'partLibrary/viewer/geometricInterrogation/EDIT_INTERROGATION_SETUP',
  RECEIVE_QUERY: 'partLibrary/viewer/geometricInterrogation/RECEIVE_QUERY',
  RECEIVE_RUN_STATUS:
    'partLibrary/viewer/geometricInterrogation/RECEIVE_RUN_STATUS',
  SELECT_INTERROGATION:
    'partLibrary/viewer/geometricInterrogation/SELECT_INTERROGATION',
  RECEIVE_SEARCH_RESULTS:
    'partLibrary/viewer/geometricInterrogation/RECEIVE_SEARCH_RESULTS',
  SET_WITH_INTERROGATION_FILE:
    'partLibrary/viewer/geometricInterrogation/SET_WITH_INTERROGATION_FILE',
  SUBMIT_INTERROGATION:
    'partLibrary/viewer/geometricInterrogation/SUBMIT_INTERROGATION',
} as const;

export type ReceiveQueryPayload = {
  forceInfo?: boolean;
  material: InterrogationMaterialQuery | null;
  task: string | null;
  shouldInit?: boolean;
  useSelectedTask?: boolean;
};

/**
 * Progress of an interrogation run reported live over the task status
 * websocket (today: mapped DXF sheet metal re-runs). Tracked apart from the
 * polled part file interrogation status, which these runs do not finish.
 */
export type InterrogationRunStatus = 'running' | 'succeeded' | 'failed';

export type ReceiveRunStatusPayload = {
  partFileUuid: string;
  status: InterrogationRunStatus;
};

export type SetWithInterrogationFilePayload = {
  partFileUuid: string;
  partInterrogationFile: PartInterrogationFile;
  showAdditionalKeysExplicit: string[];
};

export const geometricInterrogation = {
  cancelEditInterrogationSetup: () => ({
    type: types.CANCEL_EDIT_INTERROGATION_SETUP,
  }),
  clearInterrogation: () => ({
    type: types.CLEAR_INTERROGATION,
  }),
  editInterrogationSetup: () => ({
    type: types.EDIT_INTERROGATION_SETUP,
  }),
  receiveQuery: ({
    forceInfo,
    material,
    task,
    shouldInit,
    useSelectedTask,
  }: ReceiveQueryPayload) => ({
    type: types.RECEIVE_QUERY,
    payload: { forceInfo, material, task, shouldInit, useSelectedTask },
  }),
  receiveRunStatus: ({ partFileUuid, status }: ReceiveRunStatusPayload) => ({
    type: types.RECEIVE_RUN_STATUS,
    payload: { partFileUuid, status },
  }),
  selectInterrogation: ({
    result,
  }: {
    result: InterrogationSelection | null;
  }) => ({
    type: types.SELECT_INTERROGATION,
    payload: { result },
  }),
  receiveSearchResults: ({
    results,
  }: {
    results: CustomInterrogationListItem[];
  }) => ({
    type: types.RECEIVE_SEARCH_RESULTS,
    payload: { results },
  }),
  setWithInterrogationFile: ({
    partFileUuid,
    partInterrogationFile,
    showAdditionalKeysExplicit,
  }: SetWithInterrogationFilePayload) => ({
    type: types.SET_WITH_INTERROGATION_FILE,
    payload: {
      partFileUuid,
      partInterrogationFile,
      showAdditionalKeysExplicit,
    },
  }),
  submitInterrogation: ({
    result,
  }: {
    result: CustomInterrogationListItem;
  }) => ({
    type: types.SUBMIT_INTERROGATION,
    payload: { result },
  }),
};

export type GeometricInterrogationAction = ActionsOf<{
  geometricInterrogation: typeof geometricInterrogation;
}>;
